import type { SocketType } from "../../types";

/**
 * The socket shape every dynamic generator emits, and the label helper they
 * share.
 *
 * Lives in `packages/workflows` rather than the backend because the generators
 * that produce it have three consumers — the backend save/read paths, the
 * editor's live config-change path, and template instantiation. Three copies of
 * a generator is three chances to derive a different socket id for the same
 * config, and since `WorkflowEdge` has no FK on its socket ids, a disagreement
 * orphans edges silently.
 */
export interface DynamicSocketDefinition {
  id: string;
  name: string;
  type: SocketType;
  required: boolean;
  description?: string;
}

/**
 * Read a step-config field that should hold the list of entries a dynamic
 * socket family is generated from.
 *
 * These generators run on config that has not necessarily been validated:
 * `canonicalizeStepSockets` regenerates sockets from the client-sent config,
 * and `updateWorkflowDefinition` defaults `validateGraph` to false. A
 * non-array value would otherwise throw inside the generator's `.filter()` /
 * `.map()` and surface as a 500 on save, so it degrades to "this step has no
 * dynamic sockets" instead.
 */
export function configEntryList<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/** `some_socket_id` → `Some Socket Id`, for generated socket labels. */
export function prettify(id: string): string {
  return id
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
